// Process Flow at run time — the wire contract, and the four calls.
//
// Everything here rides the transaction screen's existing `execute` funnel
// (`runtime.execute(obj, 'process_trail', …)`), so the package carries no HTTP
// client of its own and the platform Run view, the designer preview, a
// smart-page embed and a downloaded copy of the app all serve these without a
// line of transport each. The server-side twin is
// `twasta_runtime_engine/process_trail.py`.

import type { ActionDef, ColumnDef } from '../types'

/** One value in a card, with the column that says how to format it.
 *
 *  Values travel RAW: a date or an amount rendered server-side with `str()`
 *  would read differently from the SAME column two lines above in the grid.
 *  The client formats through the one shared display-format renderer
 *  (`<DisplayCell>`), which is why the column rides along. */
export interface FeedCell {
  /** A constant authored into the well (an object's title, a caption). */
  text?: string
  value?: unknown
  col?: ColumnDef
  /** A heading the author put in front of this value. */
  label?: string
}

/** The Feeds visual's six wells, as the server resolved them for this record. */
export interface FeedCardRows {
  first_left?: FeedCell[]
  first_right?: FeedCell[]
  second_left?: FeedCell[]
  second_right?: FeedCell[]
  third_left?: FeedCell[]
  third_right?: FeedCell[]
}

/** The header of one record's card. Named ...Model because the COMPONENT that
 *  draws it is `FeedCard`, and one name cannot be both in a barrel export. */
export interface FeedCardModel {
  icon?: string
  rows: FeedCardRows
  /** Whether this record's form declares a narrative or detail rows. False ⇒
   *  the card has no body to open, so no twisty is drawn. */
  has_body?: boolean
}

/** A card's body, fetched when it is expanded — see `processCardBody`. */
export interface FeedBody {
  narrative: FeedCell[]
  detail?: {
    columns: ColumnDef[]
    rows: FeedCell[][]
    total: number
    shown: number
  } | null
  restricted?: boolean
}

/**
 * What the HOST lets a viewer do to a record shown inside a process screen.
 *
 * Threaded in rather than rebuilt: running an action button is not one call, it
 * is pre-validations, a confirmation gate, warnings the user may acknowledge,
 * and a result that can rewrite the form — all of which the transaction screen
 * already implements, rights-gated, for its OWN object. So the process screens
 * borrow those handlers instead of growing a second, subtly different copy.
 *
 * They apply to `objName` alone. A record of a DIFFERENT object is offered what
 * can honestly be offered from here — see `buildRecordActions`.
 */
export interface NodeActions {
  objName: string
  rowActions?: ActionDef[]
  /** The object's merged system actions. A `system_op: 'edit'` action carries
   *  the conditions that decide whether a record may be opened for editing at
   *  all, so a process surface opens records THROUGH it — see `openRecord`.
   *  Without this the trail's Edit would skip a rule the list enforces. */
  systemOverrides?: { add?: ActionDef; edit?: ActionDef; view?: ActionDef; delete?: ActionDef }
  onRunRowAction?: (name: string, txnId: string) => void | Promise<void>
  onAdd?: () => void
  onEdit?: (txnId: string) => void
  onDelete?: (txnId: string) => void
  onHistory?: (txnId: string) => void
  onAlias?: (txnId: string) => void
  onAssign?: (txnId: string) => void
  canAdd?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

/** Which record this is, when it happened, and who last touched it.
 *
 *  A card is laid out by its object's author and may say anything; this line
 *  says the three things the design view has to show for records of six
 *  different objects, resolved server-side from the key column and the columns
 *  a save stamps. `who` arrives already resolved to a person's name where the
 *  application knows them — the raw 10-character stamp otherwise. */
export interface RecordStamp {
  ident?: FeedCell
  when?: FeedCell
  who?: string
}

/** One step in a record's chain. A node the viewer may not read keeps its
 *  place and its children — breaking the chain there would hide the far end
 *  they ARE entitled to — and carries no txn_id, no card and no count. */
export interface TrailNode {
  obj_name: string
  title: string
  txn_id?: string
  readable?: boolean
  restricted?: boolean
  exists?: boolean
  is_anchor?: boolean
  card?: FeedCardModel
  stamp?: RecordStamp
  /** The engine's own per-row verdicts, so the trail gates a record exactly as
   *  its list does. Absent ⇒ editable, nothing hidden. */
  editable?: boolean
  lock_reason?: string
  hidden_actions?: string[]
  children: TrailNode[]
}

/** What a box IS, beside which object it points at.
 *
 *  `step`, `entity` and `store` are all ordinary transaction objects with their
 *  own screens and their own records — what separates them is how the process
 *  treats them: somebody works through a step, reads an entity, and the process
 *  WRITES a store without anybody typing into it. A `process` hands over to
 *  another diagram and an `external` is a party outside the application; those
 *  two have no records, which is why the engine's walk keeps only obj_type
 *  "T". */
export type NodeKind = 'step' | 'entity' | 'store' | 'process' | 'external'

export interface FlowNode {
  id: string
  obj_name: string
  obj_type: string
  /** Absent on a diagram drawn before kinds existed — every node in one of
   *  those is a step, which is what the default reads as. */
  kind?: NodeKind
  /** What the application's own MENU calls this step — the diagram's authored
   *  label only where the menu has no entry for it. */
  label: string
  /** The menu entry's description and icon, so a step on a diagram looks like
   *  the thing people click in the sidebar. */
  description?: string
  icon?: string
  /** process: the flow handed to, and the step it starts at. */
  flow?: string
  entry_obj?: string
  x: number
  y: number
  readable?: boolean
}

export interface FlowEdge {
  id: string
  from: string
  to: string
  /** `work` (the next step), `link` (a related record), `update` (writes a data
   *  store) or `read` (looks one up). */
  kind: string
  label?: string
  /** False when the arrow has no record match — it draws, but no trail walks
   *  it. Shown to a designer rather than silently returning an empty chain. */
  joined?: boolean
  /** False on a next-step arrow that deliberately raises no task: it says what
   *  follows what, and asks nobody to do anything. */
  raises_task?: boolean
}

export interface FlowSummary {
  name: string
  title: string
  description?: string
  nodes: FlowNode[]
  edges: FlowEdge[]
}

export interface FlowsResponse {
  ok: boolean
  flows: FlowSummary[]
  obj_name: string
  /** The flows this object appears on — empty ⇒ no trail to offer. */
  in_flows: string[]
}

export interface TrailResponse {
  ok: boolean
  flow: { name: string; title: string } | null
  flows?: FlowSummary[]
  anchor: { obj_name: string; txn_id: string }
  roots: TrailNode[]
  truncated?: boolean
}

export interface NodeDataResponse {
  ok: boolean
  obj_name: string
  title?: string
  restricted?: boolean
  cards: Array<{
    txn_id: string
    card: FeedCardModel
    stamp?: RecordStamp
    /** The engine's own per-row verdicts, so this panel gates a record exactly
     *  as its list does. Absent ⇒ editable, nothing hidden. */
    editable?: boolean
    lock_reason?: string
    hidden_actions?: string[]
  }>
  total: number
  offset?: number
}

/** The one call shape every process action shares. */
type Exec = (objName: string, action: string, payload?: Record<string, unknown>)
  => Promise<unknown>

export function processFlows(exec: Exec, objName: string): Promise<FlowsResponse> {
  return exec(objName, 'process_flows', {}) as Promise<FlowsResponse>
}

export function processTrail(
  exec: Exec, objName: string, txnId: string, flow?: string,
): Promise<TrailResponse> {
  return exec(objName, 'process_trail',
    { txn_id: txnId, ...(flow ? { flow } : {}) }) as Promise<TrailResponse>
}

/** ONE PAGE of a node's records. `limit`/`offset` are the list action's own,
 *  so the process view pages exactly as the object's list does; the engine
 *  caps a page at 200, the largest size the shared footer offers. */
export function processNodeData(
  exec: Exec, objName: string, limit = 50, offset = 0,
): Promise<NodeDataResponse> {
  return exec(objName, 'process_node_data',
    { node_obj: objName, limit, offset }) as Promise<NodeDataResponse>
}

export function processCardBody(
  exec: Exec, objName: string, txnId: string,
): Promise<FeedBody> {
  return exec(objName, 'process_card_body',
    { card_obj: objName, txn_id: txnId }) as Promise<FeedBody>
}
